import React, { useEffect, useState } from "react";
import { Modal, Form, Input, message, Select, Row, Col, Switch, Upload, Button, Spin } from "antd";
import { UploadOutlined, UserOutlined, EditOutlined, PlusOutlined, BankOutlined } from "@ant-design/icons";
import DeliveryStaffServ from "../service/DeliveryStaffServ";
import { getImageUrl } from "./SafeImage";

const { Option } = Select;

const AddDeliveryStaff = ({ open, details, isEditing, onClose, refresh }) => {
    const [form] = Form.useForm();
    const [bankForm] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [imagePreview, setImagePreview] = useState("");
    const [bankModalOpen, setBankModalOpen] = useState(false);
    
    // Watch bankDetails from main form to re-render the display block
    const bankDetailsValue = Form.useWatch('bankDetails', form);

    useEffect(() => {
        if (isEditing && details) {
            form.setFieldsValue({
                firstName: details.firstName,
                lastName: details.lastName,
                email: details.email,
                phone: details.phone,
                vehicleType: details.vehicleType,
                vehicleNumber: details.vehicleNumber,
                bankDetails: details.bankDetails || null,
                isActive: details.isActive !== false,
                profilePhoto: details.profilePhoto || details.profileImage || details.imageUrl || ""
            });
            setImagePreview(getImageUrl(details.profilePhoto || details.profileImage || details.imageUrl) || "");
        } else {
            form.resetFields();
            form.setFieldsValue({ isActive: true });
            setImagePreview("");
        }
    }, [isEditing, details, open, form]);

    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const handleImageUpload = async (file) => {
        try {
            setIsUploadingImage(true);
            const formData = new FormData();
            formData.append("image", file);
            const res = await DeliveryStaffServ.uploadImage(formData);
            if (res && res.imageUrl) {
                setImagePreview(res.imageUrl);
                form.setFieldsValue({ profilePhoto: res.imageUrl });
                message.success("Image uploaded successfully");
            }
        } catch (error) {
            message.error("Failed to upload image");
        } finally {
            setIsUploadingImage(false);
        }
        return false;
    };

    const handleBankSubmit = async () => {
        try {
            const values = await bankForm.validateFields();
            form.setFieldsValue({ bankDetails: values });
            setBankModalOpen(false);
        } catch (err) {
            console.error("Bank form validation failed:", err);
        }
    };

    const openBankModal = () => {
        bankForm.setFieldsValue(form.getFieldValue('bankDetails') || {});
        setBankModalOpen(true);
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const values = await form.validateFields();

            if (isEditing && details?._id) {
                await DeliveryStaffServ.update(details._id, values);
                message.success("Staff member updated successfully");
            } else {
                await DeliveryStaffServ.create(values);
                message.success("Staff member added successfully");
            }
            refresh();
            onClose();
        } catch (error) {
            console.error("Error adding staff:", error);
            if (!error.errorFields) {
                message.error("Failed to save staff member");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const labelStyle = { fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 4 };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={550}
            centered
            closable={false}
            className="ss-modal"
            bodyStyle={{ padding: 0 }}
            maskStyle={{ backdropFilter: 'blur(3px)' }}
            style={{ borderRadius: 12, overflow: 'hidden' }}
            styles={{ content: { padding: 0, borderRadius: 12, overflow: 'hidden' }, body: { padding: 0 } }}
        >
            <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
                {/* Header Section */}
                <div style={{ 
                    padding: '12px 16px', 
                    background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)', 
                    borderBottom: '1px solid #e2e8f0', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#1e293b' }}>
                            {isEditing ? "Edit Staff Details" : "Create New Staff"}
                        </h2>
                        <p style={{ margin: '2px 0 0', fontSize: 10, color: '#64748b', fontWeight: 600 }}>
                            {isEditing ? "Update account information" : "Fill in details to register staff member"}
                        </p>
                    </div>
                    <button 
                        onClick={onClose}
                        style={{ background: '#fff', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 12, width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                    >
                        <span style={{ fontSize: 12 }}>×</span>
                    </button>
                </div>

                {/* Form Content */}
                <div style={{ padding: '16px 20px', maxHeight: '70vh', overflowY: 'auto' }}>
                    <Form layout="vertical" form={form} requiredMark={false} size="small">
                        <Row gutter={16}>
                            {/* Left Column: Profile Image */}
                            <Col xs={24} md={8} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: 10 }}>
                                <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: 12 }}>
                                    {imagePreview ? (
                                        <img src={imagePreview} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <UserOutlined style={{ fontSize: 24, color: '#94a3b8' }} />
                                    )}
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <Form.Item name="profilePhoto" style={{ marginBottom: 0 }}>
                                        <Upload beforeUpload={handleImageUpload} showUploadList={false} accept="image/*">
                                            <Button icon={isUploadingImage ? <Spin size="small" /> : <UploadOutlined />} size="small" style={{ borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                                                {isUploadingImage ? "Uploading..." : "Upload Photo"}
                                            </Button>
                                        </Upload>
                                    </Form.Item>
                                    <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 4 }}>Recommended: 200x200px</div>
                                </div>
                            </Col>

                            {/* Right Column: Name Fields */}
                            <Col xs={24} md={16}>
                                <Row gutter={12}>
                                    <Col xs={24}>
                                        <Form.Item
                                            name="firstName"
                                            label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>First Name <span style={{color: '#ef4444'}}>*</span></span>}
                                            rules={[{ required: true, message: "Required" }]}
                                            style={{ marginBottom: 8 }}
                                        >
                                            <Input placeholder="First name" style={{ borderRadius: 4 }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item
                                            name="lastName"
                                            label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Last Name <span style={{color: '#ef4444'}}>*</span></span>}
                                            rules={[{ required: true, message: "Required" }]}
                                            style={{ marginBottom: 8 }}
                                        >
                                            <Input placeholder="Last name" style={{ borderRadius: 4 }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="email"
                                    label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Email Address <span style={{color: '#ef4444'}}>*</span></span>}
                                    rules={[
                                        { required: true, message: "Required" },
                                        { type: "email", message: "Valid email required" }
                                    ]}
                                    style={{ marginBottom: 8 }}
                                >
                                    <Input placeholder="staff@example.com" style={{ borderRadius: 4 }} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="phone"
                                    label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Phone Number <span style={{color: '#ef4444'}}>*</span></span>}
                                    rules={[{ required: true, message: "Required" }]}
                                    style={{ marginBottom: 8 }}
                                >
                                    <Input placeholder="+1 (555) 000-0000" style={{ borderRadius: 4 }} />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="vehicleType"
                                    label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Vehicle Type <span style={{color: '#ef4444'}}>*</span></span>}
                                    rules={[{ required: true, message: "Required" }]}
                                    style={{ marginBottom: 8 }}
                                >
                                    <Select placeholder="Select vehicle" style={{ borderRadius: 4 }}>
                                        <Option value="cycle">Cycle</Option>
                                        <Option value="bike">Bike</Option>
                                        <Option value="electric bike">Electric Bike</Option>
                                        <Option value="car">Car</Option>
                                        <Option value="van">Van</Option>
                                        <Option value="truck">Truck</Option>
                                        <Option value="other">Other</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="vehicleNumber"
                                    label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Vehicle Number <span style={{color: '#ef4444'}}>*</span></span>}
                                    rules={[{ required: true, message: "Required" }]}
                                    style={{ marginBottom: 8 }}
                                >
                                    <Input placeholder="e.g. MH01AB1234" style={{ borderRadius: 4 }} />
                                </Form.Item>
                            </Col>

                            <Col xs={24}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                    <span style={{...labelStyle, fontSize: 9, marginBottom: 0}}>Bank Details</span>
                                    {(!bankDetailsValue || Object.keys(bankDetailsValue).length === 0) && (
                                        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={openBankModal} style={{ borderRadius: 4, fontSize: 10, padding: '0 8px', height: 24, color: '#6366f1', borderColor: '#c7d2fe', background: '#eef2ff' }}>
                                            Add Bank
                                        </Button>
                                    )}
                                </div>
                                <Form.Item name="bankDetails" noStyle>
                                    <Input type="hidden" />
                                </Form.Item>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginBottom: 12 }}>
                                    {(bankDetailsValue && Object.keys(bankDetailsValue).length > 0) ? (
                                        <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 8, position: 'relative', background: '#f8fafc' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                                                <BankOutlined style={{ color: '#6366f1', fontSize: 10 }} />
                                                <span style={{ fontSize: 9, fontWeight: 700, color: '#1e293b', textTransform: 'uppercase' }}>
                                                    {bankDetailsValue.bankName || 'Bank Details'}
                                                </span>
                                            </div>
                                            <p style={{ margin: 0, fontSize: 10, color: '#64748b', lineHeight: 1.4 }}>
                                                <strong>A/C Holder:</strong> {bankDetailsValue.accountHolderName}<br/>
                                                <strong>A/C Number:</strong> {bankDetailsValue.accountNumber}<br/>
                                                <strong>IFSC:</strong> {bankDetailsValue.ifscCode}
                                            </p>
                                            <button 
                                                type="button"
                                                onClick={openBankModal}
                                                style={{ position: 'absolute', bottom: 8, right: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', color: '#6366f1', padding: 4, display: 'flex', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                                            >
                                                <EditOutlined style={{ fontSize: 10 }} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={{ padding: '12px', textAlign: 'center', background: '#f8fafc', borderRadius: 6, border: '1px dashed #cbd5e1', fontSize: 10, color: '#94a3b8' }}>
                                            No bank details saved. Click 'Add Bank' to create one.
                                        </div>
                                    )}
                                </div>
                            </Col>

                            {isEditing && (
                                <Col xs={12}>
                                    <Form.Item shouldUpdate={(prev, curr) => prev.isActive !== curr.isActive} noStyle>
                                        {({ getFieldValue }) => (
                                            <Form.Item
                                                name="isActive"
                                                label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Active Status</span>}
                                                valuePropName="checked"
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Switch size="small" style={{ background: getFieldValue('isActive') ? '#10b981' : '#cbd5e1' }} />
                                            </Form.Item>
                                        )}
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                    </Form>
                </div>

                {/* Footer Section */}
                <div style={{ padding: '12px 16px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                    <button 
                        onClick={onClose}
                        disabled={submitting}
                        style={{
                            padding: '6px 16px', borderRadius: 6, border: '1px solid #e2e8f0',
                            background: '#fff', color: '#64748b', fontSize: 11, fontWeight: 700,
                            cursor: submitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSubmit}
                        disabled={submitting}
                        style={{
                            padding: '6px 20px', borderRadius: 6, border: 'none',
                            background: submitting ? '#9ca3af' : '#4F46E5', color: '#fff', fontSize: 11, fontWeight: 700,
                            cursor: submitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(79, 70, 229, 0.2)'
                        }}
                    >
                        {submitting ? 'Saving...' : (isEditing ? 'Update Staff' : 'Save Staff')}
                    </button>
                </div>
            </div>

            {/* Bank Details Modal */}
            <Modal
                open={bankModalOpen}
                onCancel={() => setBankModalOpen(false)}
                footer={null}
                width={450}
                centered
                closable={false}
                className="ss-modal"
                bodyStyle={{ padding: 0 }}
                maskStyle={{ backdropFilter: 'blur(3px)' }}
                style={{ borderRadius: 12, overflow: 'hidden' }}
                styles={{ content: { padding: 0, borderRadius: 12, overflow: 'hidden' }, body: { padding: 0 } }}
                destroyOnClose
            >
                <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
                    {/* Header Section */}
                    <div style={{ 
                        padding: '12px 16px', 
                        background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)', 
                        borderBottom: '1px solid #e2e8f0', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center' 
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <BankOutlined style={{ color: '#4F46E5', fontSize: 16 }} />
                            <div>
                                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#1e293b' }}>
                                    Edit Bank Details
                                </h2>
                                <p style={{ margin: '2px 0 0', fontSize: 10, color: '#64748b', fontWeight: 600 }}>
                                    Update banking information
                                </p>
                            </div>
                        </div>
                        <button 
                            onClick={() => setBankModalOpen(false)}
                            style={{ background: '#fff', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 12, width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                        >
                            <span style={{ fontSize: 12 }}>×</span>
                        </button>
                    </div>

                    {/* Form Content */}
                    <div style={{ padding: '16px 20px', maxHeight: '70vh', overflowY: 'auto' }}>
                        <Form form={bankForm} layout="vertical" onFinish={handleBankSubmit} size="small" requiredMark={false}>
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item 
                                        name="bankName" 
                                        label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Bank Name <span style={{color: '#ef4444'}}>*</span></span>}
                                        rules={[{ required: true, message: 'Please enter bank name' }]}
                                        style={{ marginBottom: 12 }}
                                    >
                                        <Input placeholder="e.g. State Bank of India" style={{ borderRadius: 4 }} />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item 
                                        name="accountHolderName" 
                                        label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Account Holder Name <span style={{color: '#ef4444'}}>*</span></span>}
                                        rules={[{ required: true, message: 'Please enter account holder name' }]}
                                        style={{ marginBottom: 12 }}
                                    >
                                        <Input placeholder="Member Name" style={{ borderRadius: 4 }} />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item 
                                        name="accountNumber" 
                                        label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>Account Number <span style={{color: '#ef4444'}}>*</span></span>}
                                        rules={[
                                            { required: true, message: 'Please enter account number' },
                                            { pattern: /^\d{9,18}$/, message: 'Please enter a valid 9-18 digit account number' }
                                        ]}
                                        style={{ marginBottom: 12 }}
                                    >
                                        <Input placeholder="XXXX XXXX XXXX" style={{ borderRadius: 4 }} />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item 
                                        name="ifscCode" 
                                        label={<span style={{...labelStyle, fontSize: 9, marginBottom: 2}}>IFSC Code <span style={{color: '#ef4444'}}>*</span></span>}
                                        rules={[{ required: true, message: 'Please enter IFSC code' }]}
                                        style={{ marginBottom: 12 }}
                                    >
                                        <Input placeholder="SBIN0001234" style={{ borderRadius: 4 }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    {/* Footer Section */}
                    <div style={{ padding: '12px 16px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                        <button 
                            onClick={() => setBankModalOpen(false)}
                            style={{
                                padding: '6px 16px', borderRadius: 6, border: '1px solid #e2e8f0',
                                background: '#fff', color: '#64748b', fontSize: 11, fontWeight: 700,
                                cursor: 'pointer', transition: 'all 0.2s'
                            }}
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleBankSubmit}
                            style={{
                                padding: '6px 20px', borderRadius: 6, border: 'none',
                                background: '#4F46E5', color: '#fff', fontSize: 11, fontWeight: 700,
                                cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(79, 70, 229, 0.2)'
                            }}
                        >
                            Save Bank Details
                        </button>
                    </div>
                </div>
            </Modal>
        </Modal>
    );
};

export default AddDeliveryStaff;
